"use client";

import * as React from "react";
import { ArrowRight, Heart, Star, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AddToCartButton,
  EcommerceProductsProvider,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

const fashionMeta = [
  {
    badge: "TRENDING",
    brand: "LUSH SIGNATURE",
    spec: "Silk Blend | Breathable | Evening Wear",
    rating: 4.9,
    reviews: 210,
    capacity: 1,
    hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/silk_evening_gown_detail.webp",
    hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/silk_evening_gown_detail.webp-wvc-srcset",
    hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/silk_evening_gown_detail.webp-wvc-sizes",
  },
  {
    badge: "NEW",
    brand: "URBAN CHIC",
    spec: "Organic Cotton | Oversized | Daily",
    rating: 4.8,
    reviews: 95,
    capacity: 1,
    hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/urban_cotton_shirt_detail.png",
    hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/urban_cotton_shirt_detail.png-wvc-srcset",
    hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/urban_cotton_shirt_detail.png-wvc-sizes",
  },
  {
    badge: "",
    brand: "MODERN MINIMAL",
    spec: "Wool Blend | Tailored | Winter",
    rating: 4.7,
    reviews: 156,
    capacity: 1,
    hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/tailored_wool_coat_detail.webp",
    hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/tailored_wool_coat_detail.webp-wvc-srcset",
    hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/tailored_wool_coat_detail.webp-wvc-sizes",
  },
  {
    badge: "TRENDING",
    brand: "LUSH SIGNATURE",
    spec: "Denim | High-Rise | Classic",
    rating: 4.6,
    reviews: 340,
    capacity: 1,
    hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/high_rise_denim_detail.png",
    hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/high_rise_denim_detail.png-wvc-srcset",
    hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/high_rise_denim_detail.png-wvc-sizes",
  },
  {
    badge: "NEW",
    brand: "LUXE LOUNGE",
    spec: "Cashmere | Soft | Comfort",
    rating: 5.0,
    reviews: 42,
    capacity: 1,
    hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/cashmere_lounge_set_detail.webp",
    hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/cashmere_lounge_set_detail.webp-wvc-srcset",
    hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/cashmere_lounge_set_detail.webp-wvc-sizes",
  },
  {
    badge: "",
    brand: "VINTAGE VIBE",
    spec: "Linen | Relaxed | Summer",
    rating: 4.5,
    reviews: 112,
    capacity: 1,
    hoverImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/linen_summer_dress_detail.png",
    hoverSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/linen_summer_dress_detail.png-wvc-srcset",
    hoverSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/linen_summer_dress_detail.png-wvc-sizes",
  },
];

function CapacityIcons({ count }: { count: number }) {
  const display = Math.min(count, 4);
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: display }).map((_, i) => (
        <Users
          key={i}
          data-index={i}
          className="size-3.5 text-muted-foreground"
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function StarRating({ rating, reviews }: { rating: number; reviews: number }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            data-index={i}
            className={
              i < Math.round(rating)
                ? "size-3.5 fill-[var(--accent)] text-[var(--accent)]"
                : "size-3.5 text-muted-foreground/40"
            }
            strokeWidth={1.5}
          />
        ))}
      </div>
      <span className="font-mono text-xs text-muted-foreground">
        {rating.toFixed(1)} ({reviews})
      </span>
    </div>
  );
}

function FeaturedProductsConsumer() {
  const { products, loading, error, setSorting, refetch } =
    useEcommerceProducts();
  const [view, setView] = React.useState<"grid" | "list">("grid");
  const [sort, setSort] = React.useState<string>("popularity");

  const handleSortChange = (value: string) => {
    setSort(value);
    switch (value) {
      case "price_asc":
        setSorting("price", "asc");
        break;
      case "price_desc":
        setSorting("price", "desc");
        break;
      case "newest":
        setSorting("date", "desc");
        break;
      case "popularity":
      default:
        setSorting("date", "desc");
        break;
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="aspect-[4/5] w-full animate-pulse bg-muted/60 rounded-lg" />
            <div className="h-4 w-2/3 animate-pulse bg-muted/60 rounded-lg" />
            <div className="h-3 w-1/3 animate-pulse bg-muted/60 rounded-lg" />
            <div className="h-3 w-1/2 animate-pulse bg-muted/60 rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-border bg-card p-8 text-center rounded-lg">
        <p className="font-default text-sm text-muted-foreground">
          Unable to load products.{" "}
          <Button
            onClick={refetch}
            variant="link"
            className="h-auto p-0 font-default text-sm text-primary"
          >
            Try again
          </Button>
        </p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="border border-border bg-card p-12 text-center rounded-lg">
        <p className="font-default text-base text-muted-foreground">
          No products available at the moment. Check back soon for new arrivals.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="mb-12 flex flex-col items-start justify-between gap-6 border-y border-border py-5 md:flex-row md:items-center">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Showing
          </span>
          <span className="font-default text-sm font-semibold text-foreground">
            {products.length} {products.length === 1 ? "item" : "items"}
          </span>
          <Badge
            variant="outline"
            className="ml-1 border-border font-mono text-[10px] uppercase tracking-wider text-muted-foreground rounded-md"
          >
            3 filters
          </Badge>
        </div>

        <div className="flex w-full flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4 md:w-auto">
          <Select value={sort} onValueChange={handleSortChange}>
            <SelectTrigger
              size="sm"
              className="w-full rounded-lg border-border bg-transparent font-mono text-xs uppercase tracking-wider sm:w-[200px]"
            >
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="rounded-lg border-border bg-popover">
              <SelectItem value="popularity" className="font-mono text-xs uppercase tracking-wider">
                Most Popular
              </SelectItem>
              <SelectItem value="newest" className="font-mono text-xs uppercase tracking-wider">
                Newest Arrivals
              </SelectItem>
              <SelectItem value="price_asc" className="font-mono text-xs uppercase tracking-wider">
                Price: Low to High
              </SelectItem>
              <SelectItem value="price_desc" className="font-mono text-xs uppercase tracking-wider">
                Price: High to Low
              </SelectItem>
            </SelectContent>
          </Select>

          <ToggleGroup
            type="single"
            value={view}
            onValueChange={(v) => v && setView(v as "grid" | "list")}
            variant="outline"
            size="sm"
            className="border-border rounded-lg"
          >
            <ToggleGroupItem
              value="grid"
              aria-label="Grid view"
              className="font-mono text-xs uppercase tracking-wider data-[state=on]:bg-foreground data-[state=on]:text-background rounded-lg"
            >
              Grid
            </ToggleGroupItem>
            <ToggleGroupItem
              value="list"
              aria-label="List view"
              className="font-mono text-xs uppercase tracking-wider data-[state=on]:bg-foreground data-[state=on]:text-background rounded-lg"
            >
              List
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      <div
        className={
          view === "grid"
            ? "grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3"
            : "flex flex-col gap-10"
        }
      >
        {products.map((product, i) => {
          const meta = fashionMeta[i % fashionMeta.length];
          return (
            <article
              key={product.id}
              data-index={i}
              className="group relative flex flex-col"
            >
              <Link to={product.link} className="block">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted rounded-lg shadow-md">
                  <img
                    src={product.featured_image_url || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722769/img/fashion_placeholder.webp"}
                    alt={product.title}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out group-hover:opacity-0"
                  />
                  <img
                    src={meta.hoverImage}
                    data-wvc-srcset={meta.hoverSrcset}
                    data-wvc-sizes={meta.hoverSizes}
                    alt={`${product.title} detail view`}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700 ease-out group-hover:opacity-100"
                  />

                  {meta.badge && (
                    <div className="absolute left-4 top-4">
                      <span
                        className={
                          meta.badge === "TRENDING"
                            ? "inline-flex items-center bg-accent px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-accent-foreground rounded-md"
                            : "inline-flex items-center bg-foreground px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-background rounded-md"
                        }
                      >
                        {meta.badge}
                      </span>
                    </div>
                  )}

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    aria-label="Add to wishlist"
                    className="absolute right-4 top-4 size-9 bg-background/90 text-foreground backdrop-blur-sm hover:bg-background hover:text-primary rounded-lg"
                  >
                    <Heart className="size-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </Link>

              <div className="mt-5 flex flex-1 flex-col">
                <Link to={product.link} className="mt-3 block">
                  <h3 className="font-default text-xl font-semibold leading-tight text-foreground transition-colors hover:text-primary">
                    {product.title}
                  </h3>
                </Link>

                <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                  {meta.brand}
                </p>

                <p className="mt-3 font-default text-sm text-muted-foreground">
                  {meta.spec}
                </p>

                <div className="mt-3">
                  <StarRating rating={meta.rating} reviews={meta.reviews} />
                </div>

                <div className="mt-4 flex items-baseline gap-2">
                  {product.discounted_price ? (
                    <>
                      <span className="font-default text-xl font-semibold text-foreground">
                        ${product.discounted_price}
                      </span>
                      <span className="font-mono text-sm text-muted-foreground line-through">
                        ${product.price}
                      </span>
                    </>
                  ) : (
                    <span className="font-default text-xl font-semibold text-foreground">
                      ${product.price}
                    </span>
                  )}
                </div>

                <div className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4">
                  <Link
                    to={product.link}
                    className="group/link inline-flex items-center gap-1.5 font-mono text-xs font-semibold uppercase tracking-widest text-foreground transition-colors hover:text-primary"
                  >
                    View Details
                    <ArrowRight
                      className="size-3.5 transition-transform group-hover/link:translate-x-1"
                      strokeWidth={2}
                    />
                  </Link>
                  <AddToCartButton
                    productId={product.id}
                    variant="default"
                    size="sm"
                    text="Add"
                    showQuantity={false}
                    buttonClassName="bg-primary text-primary-foreground rounded-lg"
                  />
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}

export default function RelatedProducts() {
  const wp_query_params = {
    per_page: 6,
    product_cat: 16,
    orderby: "date",
    order: "desc" as const,
  };
  const wp_query = new WP_Query(wp_query_params);

  return (
    <section data-section-id="441"
      id="relatedproducts"
      className="bg-background py-28"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="mb-14 flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-accent">
              Curated Selection
            </p>
            <h2 className="mt-5 font-default text-5xl font-semibold leading-[1.05] tracking-tight text-foreground md:text-6xl">
              Related Products
            </h2>
            <div className="mt-6 h-px w-16 bg-foreground/30" />
            <p className="mt-6 max-w-xl font-default text-base leading-relaxed text-muted-foreground">
              Discover our latest arrivals and timeless essentials, hand-picked to elevate your wardrobe with premium quality and modern style.
            </p>
          </div>

          <Link to="/ShopPage" className="hidden lg:inline-flex">
            <Button
              variant="outline"
              className="group rounded-lg border-foreground bg-transparent font-mono text-xs font-semibold uppercase tracking-widest text-foreground hover:bg-foreground hover:text-background"
            >
              Browse All
              <ArrowRight className="ml-1 size-3.5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>

        <EcommerceProductsProvider wp_query={wp_query}>
          <FeaturedProductsConsumer />
        </EcommerceProductsProvider>

        <div className="mt-12 flex justify-center lg:hidden">
          <Link to="/ShopPage">
            <Button
              variant="outline"
              className="group rounded-lg border-foreground bg-transparent font-mono text-xs font-semibold uppercase tracking-widest text-foreground hover:bg-foreground hover:text-background"
            >
              Browse All
              <ArrowRight className="ml-1 size-3.5 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}